import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import api from '../../services/api';

const ShopAdminAdDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ad, setAd] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    unit: '',
    price: '',
    currency: 'INR',
    isPermanent: false,
    startDate: '',
    endDate: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchAd();
    fetchProducts();
  }, [id]);

  const fetchAd = async () => {
    try {
      const response = await api.get(`/api/shop/ads/${id}`);
      setAd(response.data.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error fetching ad' });
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get(`/api/shop/ads/${id}/products`);
      setProducts(response.data.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      unit: '',
      price: '',
      currency: 'INR',
      isPermanent: false,
      startDate: '',
      endDate: ''
    });
    setShowProductModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      unit: product.unit,
      price: product.price,
      currency: product.currency,
      isPermanent: product.isPermanent,
      startDate: product.startDate ? new Date(product.startDate).toISOString().split('T')[0] : '',
      endDate: product.endDate ? new Date(product.endDate).toISOString().split('T')[0] : ''
    });
    setShowProductModal(true);
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.put(`/api/shop/products/${editingProduct._id}`, formData);
        setMessage({ type: 'success', text: 'Product updated successfully' });
      } else {
        await api.post(`/api/shop/ads/${id}/products`, formData);
        setMessage({ type: 'success', text: 'Product created successfully' });
      }
      setShowProductModal(false);
      fetchProducts();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error saving product' });
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await api.delete(`/api/shop/products/${productId}`);
        setMessage({ type: 'success', text: 'Product deleted successfully' });
        fetchProducts();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } catch (error) {
        setMessage({ type: 'error', text: 'Error deleting product' });
      }
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div>
        <Navbar />
        <div className="container">
          <div className="alert alert-error">Ad not found</div>
          <button className="btn btn-secondary" onClick={() => navigate('/shop/ads')}>
            Back to Ads
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Ad Details</h1>
          <button className="btn btn-secondary" onClick={() => navigate('/shop/ads')}>
            Back to Ads
          </button>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
            {message.text}
          </div>
        )}

        <div className="card">
          <h2>{ad.title}</h2>
          <p><strong>Category:</strong> {ad.categoryId?.name || 'N/A'}</p>
          <p><strong>Status:</strong> {ad.status}</p>
          <p><strong>Boosted:</strong> {ad.isBoosted ? 'Yes' : 'No'}</p>
          <p><strong>End Date:</strong> {new Date(ad.endDate).toLocaleDateString()}</p>
          {ad.description && <p><strong>Description:</strong> {ad.description}</p>}
          
          <div style={{ marginTop: '20px' }}>
            <h3>Main Image</h3>
            <img src={ad.mainImageUrl} alt={ad.title} style={{ maxWidth: '100%', height: 'auto', marginTop: '10px' }} />
          </div>

          {ad.subImageUrls && ad.subImageUrls.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h3>Sub Images</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginTop: '10px' }}>
                {ad.subImageUrls.map((url, index) => (
                  <img key={index} src={url} alt={`Sub ${index + 1}`} style={{ width: '100%', height: 'auto' }} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="page-header">
            <h2>Products</h2>
            <button className="btn btn-primary" onClick={handleCreateProduct}>
              Add Product
            </button>
          </div>

          {products.length === 0 ? (
            <p>No products added yet.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Unit</th>
                  <th>Price</th>
                  <th>Currency</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map(product => (
                  <tr key={product._id}>
                    <td>{product.name}</td>
                    <td>{product.unit}</td>
                    <td>{product.price}</td>
                    <td>{product.currency}</td>
                    <td>{product.isPermanent ? 'Permanent' : 'Limited Time'}</td>
                    <td>{product.status}</td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditProduct(product)}
                        style={{ marginRight: '10px' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDeleteProduct(product._id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showProductModal && (
          <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">
                  {editingProduct ? 'Edit Product' : 'Add Product'}
                </h2>
                <button className="close-btn" onClick={() => setShowProductModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmitProduct}>
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Unit *</label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    required
                    placeholder="e.g., 1kg, 500g"
                  />
                </div>
                <div className="form-group">
                  <label>Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    required
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <input
                    type="text"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isPermanent}
                      onChange={(e) => setFormData({ ...formData, isPermanent: e.target.checked })}
                    />
                    {' '}Permanent Offer
                  </label>
                </div>
                {!formData.isPermanent && (
                  <>
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      />
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">
                    {editingProduct ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowProductModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopAdminAdDetail;

